import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  debounceTime,
  map,
  shareReplay,
  switchMap,
} from 'rxjs';
import { SchoolDTO } from 'src/app/common/dtos/school.dto';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { SchoolSearchResponse } from 'src/app/common/types/responses/responses';
import { UserAPIResponse } from '../../types/responses/user.responses';

@Injectable({
  providedIn: 'root',
})
export class SchoolSearchService {
  items: Observable<SchoolDTO[]>;

  currentResponse: SchoolSearchResponse;

  districtId: number;

  private term: Subject<string> = new Subject<string>();

  constructor(private apiService: APICoreService) {
    this.items = this.term.pipe(
      debounceTime(250),

      switchMap((term: string) =>
        this.findSchools(term, 10000, 1, this.districtId)
      ),
      shareReplay(1)
    );
  }

  search(term: string, districtId = 0) {
    /**
     * Set the value which the search stream will work.
     */
    this.districtId = districtId;
    return this.term.next(term);
  }

  complete(): void {
    this.term.complete();
  }

  public findSchools(
    searchTerm = '',
    per_page = 20,
    page = 1,
    districtId = 0
  ): Observable<SchoolDTO[]> {
    const params: {
      search?: string;
      page: number;
      per_page: number;
      district_id?: number;
    } = {
      page,
      per_page,
    };

    if (searchTerm) {
      params.search = searchTerm;
    }

    if (districtId) {
      params.district_id = districtId;
    }

    return this.apiService.getRequest('schools', params).pipe(
      map((response) => {
        this.currentResponse = {
          schools: response.items,
          _meta: response._meta,
          _links: response._links,
        };
        return response.items;
      })
    );
  }

  public getAllSchools(districtId = 0): Observable<SchoolDTO[]> {
    const params: {
      page: number;
      per_page: number;
      district_id?: number;
    } = {
      page: 1,
      per_page: 300,
    };
    if (districtId) {
      params.district_id = districtId;
    }

    return this.apiService.getRequest('schools', params).pipe(
      map((response) => {
        this.currentResponse = {
          schools: response.items,
          _meta: response._meta,
          _links: response._links,
        };
        return response.items;
      })
    );
  }

  public getDistrictSchools(districtId: number): Observable<SchoolDTO[]> {
    return this.apiService
      .getRequest(`schools?page=1&per_page=10000&district_id=${districtId}`)
      .pipe(
        map((response) => {
          this.currentResponse = {
            schools: response.items,
            _meta: response._meta,
            _links: response._links,
          };
          return response.items;
        })
      );
  }

  // ONLY USED BY SCHOOL SELECT COMPONENT
  getDistrictsSchools(districtIds: number[]): Observable<SchoolDTO[]> {
    const districtsString = districtIds.join('&district_ids[]=');
    return this.apiService
      .getRequest(`schools?district_ids[]=${districtsString}&per_page=1000`)
      .pipe(
        map((response) => {
          this.currentResponse = {
            schools: response.items,
            _meta: response._meta,
            _links: response._links,
          };
          return response.items;
        })
      );
  }

  getAssignedCoacheesSchools(
    assigned_coach: number,
    per_page = 100
  ): Observable<SchoolDTO[]> {
    const params = {
      assigned_coach,
      per_page,
    };

    const userSchools: SchoolDTO[] = [];

    return this.apiService.getRequest('users', params).pipe(
      map((response) => {
        response.items.forEach((user: UserAPIResponse) => {
          if (
            user.school &&
            !userSchools.some((school) => school.id === user.school?.id)
          ) {
            userSchools.push(user.school);
          }
        });
        return userSchools;
      })
    );
  }

  getAssignedSchools(userId: number) {
    return this.apiService
      .getRequest(`egrowe/user_school/${userId}`)
      .pipe(map((response) => response.items));
  }

  getSchool(id: number): Observable<SchoolDTO | null> {
    return this.apiService
      .getRequest(`schools/${id}`)
      .pipe(map((response) => response.item));
  }
}
